import React, { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  ModalOverlay,
  ModalPanel,
  ModalHead,
  ModalTitle,
  ModalClose,
  ModalSub,
  ModalFoot,
  ModalBtn,
} from './styles'

/**
 * Gerador de link rastreável (UTM) — enabler da atribuição por campanha.
 *
 * O produtor monta links da loja do evento com utm_source/medium/campaign; ao
 * compartilhá-los, as vendas vindas deles passam a aparecer na aba "Campanhas"
 * (via GA4 sessionCampaignName). Sem UTM, o GA4 só enxerga o canal, não a campanha.
 */

interface UtmBuilderModalProps {
  lojaUrl: string
  onClose: () => void
}

interface Preset {
  label: string
  source: string
  medium: string
}

const PRESETS: Preset[] = [
  { label: 'Instagram (bio)', source: 'instagram', medium: 'bio' },
  { label: 'Instagram Stories', source: 'instagram', medium: 'stories' },
  { label: 'Facebook', source: 'facebook', medium: 'social' },
  { label: 'WhatsApp', source: 'whatsapp', medium: 'social' },
  { label: 'E-mail', source: 'email', medium: 'email' },
  { label: 'Google Ads', source: 'google', medium: 'cpc' },
]

const Presets = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
`

const PresetBtn = styled.button<{ $active: boolean }>`
  border: 1px solid ${(p) => (p.$active ? '#2e5aac' : '#e6e9ee')};
  background: ${(p) => (p.$active ? '#eef2fb' : '#ffffff')};
  color: ${(p) => (p.$active ? '#2e5aac' : '#475467')};
  border-radius: 999px;
  padding: 7px 14px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    border-color: #2e5aac;
  }
`

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const FieldLabel = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #344054;
`

const FieldHint = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #98a2b3;
`

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e6e9ee;
  border-radius: 9px;
  background: #f9fafb;
  color: #101828;
  font-family: 'Inter', sans-serif;
  font-size: 14px;

  &:focus {
    outline: 2px solid #2e5aac33;
    border-color: #2e5aac;
    background: #fff;
  }
`

const UrlBox = styled.div`
  margin-top: 20px;
  background: #101828;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`

const UrlText = styled.code`
  flex: 1;
  min-width: 0;
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 12.5px;
  color: #d1e9ff;
  word-break: break-all;
`

const CopyBtn = styled.button<{ $done: boolean }>`
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
  background: ${(p) => (p.$done ? '#12b76a' : '#2e5aac')};
`

const slugify = (s: string): string =>
  String(s)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const UtmBuilderModal: FC<UtmBuilderModalProps> = ({ lojaUrl, onClose }) => {
  const [source, setSource] = useState('instagram')
  const [medium, setMedium] = useState('bio')
  const [campaign, setCampaign] = useState('')
  const [copied, setCopied] = useState(false)

  const activePreset = (p: Preset): boolean => p.source === source && p.medium === medium

  const url = useMemo(() => {
    const params: string[] = []
    if (source.trim()) params.push(`utm_source=${encodeURIComponent(slugify(source))}`)
    if (medium.trim()) params.push(`utm_medium=${encodeURIComponent(slugify(medium))}`)
    if (campaign.trim()) params.push(`utm_campaign=${encodeURIComponent(slugify(campaign))}`)
    const sep = lojaUrl.includes('?') ? '&' : '?'
    return params.length ? `${lojaUrl}${sep}${params.join('&')}` : lojaUrl
  }, [lojaUrl, source, medium, campaign])

  const copy = (): void => {
    navigator.clipboard?.writeText(url).then(
      () => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1800)
      },
      () => undefined
    )
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalPanel onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <ModalTitle>Gerar link rastreável (UTM)</ModalTitle>
          <ModalClose type="button" onClick={onClose}>
            ×
          </ModalClose>
        </ModalHead>
        <ModalSub>
          Compartilhe estes links nas suas divulgações. As vendas vindas deles passam a aparecer na
          aba <strong>Campanhas</strong>, separadas por origem.
        </ModalSub>

        <Presets>
          {PRESETS.map((p) => (
            <PresetBtn
              key={p.label}
              type="button"
              $active={activePreset(p)}
              onClick={() => {
                setSource(p.source)
                setMedium(p.medium)
              }}
            >
              {p.label}
            </PresetBtn>
          ))}
        </Presets>

        <Fields>
          <Field>
            <FieldLabel>
              Origem <FieldHint>· utm_source (ex.: instagram, whatsapp)</FieldHint>
            </FieldLabel>
            <Input value={source} onChange={(e) => setSource(e.target.value)} placeholder="instagram" />
          </Field>
          <Field>
            <FieldLabel>
              Mídia <FieldHint>· utm_medium (ex.: bio, stories, cpc)</FieldHint>
            </FieldLabel>
            <Input value={medium} onChange={(e) => setMedium(e.target.value)} placeholder="bio" />
          </Field>
          <Field>
            <FieldLabel>
              Campanha <FieldHint>· utm_campaign (ex.: lancamento-junho)</FieldHint>
            </FieldLabel>
            <Input
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              placeholder="lancamento-junho"
            />
          </Field>
        </Fields>

        <UrlBox>
          <UrlText>{url}</UrlText>
          <CopyBtn type="button" $done={copied} onClick={copy}>
            {copied ? 'Copiado!' : 'Copiar'}
          </CopyBtn>
        </UrlBox>

        <ModalFoot>
          <ModalBtn type="button" $variant="ghost" onClick={onClose}>
            Fechar
          </ModalBtn>
        </ModalFoot>
      </ModalPanel>
    </ModalOverlay>
  )
}

export default UtmBuilderModal
